import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Input,
    OnInit,
    signal,
    ViewChild,
    ViewEncapsulation,
} from "@angular/core";
import { FormBuilder, Validators } from "@angular/forms";
import { TypeManagerDecorator } from "../../../../main/type.map.service";
import { Contact } from "../../../../pojo/Contact";
import { UserService } from "../../../../services/user.service";
import { ObjDetailsData } from "../../s25.object.details.component";
import { S25EditableTextComponent } from "../../../s25-editable/s25-editable-text/s25.editable.text.component";
import { Bind } from "../../../../decorators/bind.decorator";
import { S25Util } from "../../../../util/s25-util";
import { Proto } from "../../../../pojo/Proto";
import NumericalBoolean = Proto.NumericalBoolean;

@TypeManagerDecorator("s25-ng-contact-details-user-account")
@Component({
    selector: "s25-ng-contact-details-user-account",
    template: `
        <div class="user-account-wrapper">
            @let userName = userData.userName;
            @if (!userName && !canEdit) {
                none
            } @else {
                <form [formGroup]="newUserForm">
                    <div class="c-margin-bottom--half">
                        <label for="username">Username</label>
                        <div>
                            @if (userName) {
                                <s25-ng-editable-text
                                    [max]="40"
                                    [readOnly]="!isAdmin || !canEdit"
                                    [ariaLabel]="'username'"
                                    [hasCommit]="true"
                                    formControlName="username"
                                    [onCommit]="onUsernameUpdate"
                                    (onCancel)="onCancel()"
                                ></s25-ng-editable-text>
                            } @else {
                                <input class="c-input" id="username" formControlName="username" />
                                @if (formSubmitted && (username.errors?.required || username.errors?.minlength)) {
                                    <p class="ngFinePrint ngRed">
                                        Please enter a username at least 3 characters in length
                                    </p>
                                }
                            }
                            @if (canEdit && ((!userName && formSubmitted) || (editableText?.editing && userName))) {
                                @if (username.errors?.duplicate) {
                                    <p class="ngFinePrint ngRed">Username "{{ username.value }}" is already in use</p>
                                }
                                <p class="ngFinePrint" [class.ngRed]="username.errors?.pattern">
                                    The following characters are not supported in usernames: ! < > % ~ ^
                                </p>
                            }
                        </div>
                    </div>
                    @if (!userName && canEdit) {
                        <div class="c-margin-bottom--half password-wrapper">
                            <label for="password">Password</label>
                            <input class="c-input" id="password" formControlName="password" />
                            @if (formSubmitted && password.errors) {
                                @if (password.errors.required || password.errors.minlength) {
                                    <p class="ngFinePrint ngRed">
                                        Please enter a password at least 5 characters in length
                                    </p>
                                }
                                @if (password.errors.pattern) {
                                    <p class="ngFinePrint ngRed">
                                        Passwords may contain only letters, numbers, and underscore characters
                                    </p>
                                }
                            }
                        </div>
                    }
                    <div class="c-margin-bottom--half">
                        <label>User Status</label>
                        @let active = userData.active;
                        @if (!canEdit) {
                            <p aria-label="User Status">{{ active ? "Active" : "Inactive" }}</p>
                        }
                        @if (canEditSecurity) {
                            <div>
                                <s25-ng-radio
                                    [value]="1"
                                    class="c-margin-right--single"
                                    formControlName="active"
                                    (modelValueChange)="onStatusChange()"
                                >
                                    Active</s25-ng-radio
                                >
                                <s25-ng-radio [value]="0" formControlName="active" (modelValueChange)="onStatusChange()"
                                    >Inactive</s25-ng-radio
                                >
                            </div>
                        }
                    </div>
                    <div class="c-margin-bottom--half security-group-wrapper">
                        <label>Security Group</label>
                        @let securityGroup = userData.securityGroup;
                        @if (canEditSecurity) {
                            <s25-ng-dropdown-search-criteria
                                [type]="'securityGroups'"
                                formControlName="securityGroup"
                                (chosenChange)="onSecurityGroupChange()"
                            ></s25-ng-dropdown-search-criteria>
                            @if (formSubmitted && security.errors) {
                                <p class="ngFinePrint ngRed">Please select a Security Group</p>
                            }
                        } @else if (userName) {
                            <p>{{ securityGroup.itemName }}</p>
                        }
                    </div>
                    @if (!userName && canEdit) {
                        <s25-ng-button [type]="'primary'" class="c-margin-top--half submit" [onClick]="saveUser"
                            >Add User</s25-ng-button
                        >
                    }
                    <s25-ng-fading-success-msg [message]="userUpdateSuccessMsg"></s25-ng-fading-success-msg>
                </form>
            }
        </div>
    `,
    styles: `
        @container useraccount (width <= 600px) {
            form {
                & > div {
                    display: flex !important;
                    flex-direction: column;
                    align-items: flex-start !important;
                    gap: 8px;

                    & > div,
                    s25-ng-dropdown-search-criteria {
                        width: 100%;
                    }

                    &:nth-child(2) {
                        display: flex;
                    }
                }

                .success-msg,
                .submit {
                    margin-left: 0 !important;
                    width: 100%;
                }
            }
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    standalone: false,
})
export class S25ContactDetailsUserAccountComponent implements OnInit {
    @Input({ required: true }) userData: Contact.DetailData["userAccount"];
    @Input({ required: true }) canEdit: boolean;
    @Input({ required: true }) canEditSecurity: boolean;
    @Input({ required: true }) isAdmin: boolean;
    @Input({ required: true }) itemId: number;

    @ViewChild(S25EditableTextComponent) editableText: S25EditableTextComponent;

    constructor(
        private fb: FormBuilder,
        private cd: ChangeDetectorRef,
    ) {}

    newUserForm = this.fb.group({
        username: ["", [Validators.required, Validators.minLength(3), Validators.pattern("^(?:(?![!<>%~^]).)*$")]],
        active: [1],
        password: ["", [Validators.required, Validators.minLength(5), Validators.pattern("^[A-Za-z0-9_]+$")]],
        securityGroup: [null as ObjDetailsData["userAccount"]["securityGroup"], [Validators.required]],
    });
    formSubmitted = false;
    userUpdateSuccessMsg = signal("");

    ngOnInit() {
        if (this.userData?.userName) {
            const { userName, securityGroup, active } = this.userData;
            this.newUserForm.setValue({
                username: userName,
                password: "",
                active: active,
                securityGroup: securityGroup,
            });
        }
    }

    async checkDuplicateUsername() {
        const [nameCheck, _] = await S25Util.Maybe(UserService.getUser(this.username.value));
        return nameCheck?.r25_username === this.username.value;
    }

    async setDupUsernameError() {
        const usernameUpdated = this.username.value !== this.userData.userName;
        if (!this.username.errors && usernameUpdated) {
            const dupUsername = await this.checkDuplicateUsername();
            dupUsername && this.username.setErrors({ duplicate: dupUsername });
        }
        return usernameUpdated;
    }

    @Bind
    async onUsernameUpdate() {
        const usernameUpdated = await this.setDupUsernameError();
        setTimeout(async () => {
            // slight delay to detect error update from s25-ng-editable-text
            if (this.username.errors) {
                this.editableText.editing = true;
                this.editableText.cdRef.markForCheck();
                return;
            }
            if (usernameUpdated) {
                const [_, error] = await S25Util.Maybe(UserService.putUsername(this.itemId, this.username.value));
                if (error) return S25Util.showError(error);
                this.userUpdateSuccessMsg.set("Username successfully updated");
                this.userData.userName = this.username.value;
            }
        });
    }

    @Bind
    async saveUser() {
        this.formSubmitted = true;
        if (this.newUserForm.invalid) return;
        await this.setDupUsernameError();
        if (!this.username.errors) {
            const { username, password, active, securityGroup } = this.newUserForm.value;
            const { itemId, itemName } = securityGroup;
            const payload = {
                contact_id: this.itemId,
                r25_username: username,
                r25_password: password,
                active: active,
                security_group: [{ security_group_id: +itemId, security_group_name: itemName, status: "new" }],
                status: "new",
            };
            const [_, error] = await S25Util.Maybe(UserService.postUser(username, payload));
            if (error) return S25Util.showError(error);
            this.userData = {
                userName: username,
                active: active as NumericalBoolean,
                securityGroup: securityGroup,
            };
            this.cd.markForCheck();
            this.userUpdateSuccessMsg.set("User account successfully created");
        }
    }

    async onStatusChange() {
        if (!this.userData.userName) return;
        const [_, error] = await S25Util.Maybe(UserService.setActiveState(this.username.value, !!this.active.value));
        if (error) return S25Util.showError(error);
        this.userUpdateSuccessMsg.set(`User status is now ${this.active.value ? "active" : "inactive"}`);
    }

    async onSecurityGroupChange() {
        if (!this.userData.userName) return;
        const { itemId, itemName } = this.security.value;
        if (+itemId !== +this.userData.securityGroup.itemId) {
            const [_, error] = await S25Util.Maybe(
                UserService.setSecurityGroup(this.username.value, +itemId, itemName),
            );
            if (error) return S25Util.showError(error);
            this.userUpdateSuccessMsg.set("Security Group successfully updated");
            this.userData.securityGroup = this.security.value;
        }
    }

    onCancel() {
        this.username.setValue(this.userData.userName);
        this.editableText.candidateVal = this.userData.userName;
    }

    get username() {
        return this.newUserForm.get("username");
    }

    get password() {
        return this.newUserForm.get("password");
    }

    get active() {
        return this.newUserForm.get("active");
    }

    get security() {
        return this.newUserForm.get("securityGroup");
    }
}
